import React, { useState, useEffect, FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Field, change, getFormValues } from "@mds/common/components/forms/form";
import { Button, Col, Row, Popconfirm } from "antd";
import { required, maxLength, requiredNewFiles } from "@mds/common/redux/utils/Validate";
import { resetForm } from "@common/utils/helpers";
import { getDropdownNoticeOfWorkApplicationDocumentTypeOptions } from "@mds/common/redux/selectors/staticContentSelectors";
import { getDraftPermitAmendmentForNOW } from "@mds/common/redux/selectors/permitSelectors";
import { isFileReferencedInConditions } from "@mds/common/utils/permitPackageDocuments";
import { NOTICE_OF_WORK_DOCUMENT } from "@mds/common/constants/API";
import { DOCUMENT, EXCEL, IMAGE, SPATIAL } from "@/constants/fileTypes";
import * as FORM from "@/constants/forms";
import FormWrapper from "@mds/common/components/forms/FormWrapper";
import { closeModal } from "@mds/common/redux/actions/modalActions";
import RenderSelect from "@mds/common/components/forms/RenderSelect";
import RenderAutoSizeField from "@mds/common/components/forms/RenderAutoSizeField";
import RenderCheckbox from "@mds/common/components/forms/RenderCheckbox";
import RenderField from "@mds/common/components/forms/RenderField";
import RenderDate from "@mds/common/components/forms/RenderDate";
import RenderFileUpload from "@mds/common/components/forms/RenderFileUpload";
import { IMineDocument, INoWDocument, IPermitAmendment } from "@mds/common/interfaces";
import { useFeatureFlag } from "@mds/common/providers/featureFlags/useFeatureFlag";
import { Feature } from "@mds/common/utils/featureFlag";
import AuthorizationWrapper from "@mds/common/wrappers/AuthorizationWrapper";
import * as Permission from "@/constants/permissions";

const PERMIT_PACKAGE_DOCUMENT_TYPE_OPTIONS = [
  { value: "DOCUMENT", label: "Document" },
  { value: "FIGURE", label: "Figure" },
];

export interface EditNoticeOfWorkDocumentFormProps {
  onSubmit: (values) => void | Promise<void>;
  title: string;
  now_application_guid: string;
  isEditMode: boolean;
  isInCompleteStatus: boolean;
  categoriesToShow: string[];
  initialValues?: any;
}

const EditNoticeOfWorkDocumentForm: FC<EditNoticeOfWorkDocumentFormProps> = ({
  onSubmit,
  title,
  now_application_guid,
  isEditMode,
  isInCompleteStatus,
  categoriesToShow = [],
  initialValues,
}) => {
  const dispatch = useDispatch();
  const { isFeatureEnabled } = useFeatureFlag();
  const formValues = useSelector(getFormValues(FORM.EDIT_NOTICE_OF_WORK_DOCUMENT_FORM)) as INoWDocument ?? { is_final_package: false };
  const { is_final_package } = formValues;
  const dropdownNoticeOfWorkApplicationDocumentTypeOptions = useSelector(
    getDropdownNoticeOfWorkApplicationDocumentTypeOptions
  );
  const draftPermitAmendment = useSelector(getDraftPermitAmendmentForNOW) as IPermitAmendment;
  const fileGuid = initialValues?.now_application_document_xref_guid;
  const isReferencedInConditions =
    !!fileGuid && isFileReferencedInConditions(draftPermitAmendment?.conditions, fileGuid);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [confirmUncheckOpen, setConfirmUncheckOpen] = useState(false);

  const filteredDropDownOptions = dropdownNoticeOfWorkApplicationDocumentTypeOptions.filter(
    ({ subType, value }) => {
      if (categoriesToShow.length > 0) {
        return categoriesToShow.includes(subType ?? value);
      }
      return true;
    }
  );

  useEffect(() => {
    dispatch(change(FORM.EDIT_NOTICE_OF_WORK_DOCUMENT_FORM, "uploadedFiles", uploadedFiles));
  }, [uploadedFiles]);

  useEffect(() => {
    if (!is_final_package) {
      dispatch(
        change(FORM.EDIT_NOTICE_OF_WORK_DOCUMENT_FORM, "permit_package_document_type_code", undefined)
      );
    }
  }, [is_final_package]);

  const onFileLoad = (document_name: string, document_manager_guid: string) => {
    const newFile = { document_name, document_manager_guid } as IMineDocument;
    const fileAlreadyInList = uploadedFiles.includes(
      (doc) => doc.document_manager_guid === document_manager_guid
    );
    if (!fileAlreadyInList) {
      setUploadedFiles([...uploadedFiles, newFile]);
    }
    setDisabled(false);
  };

  const onRemoveFile = (err, fileItem) => {
    const remainingFiles = uploadedFiles.filter(
      (file) => file.document_manager_guid !== fileItem.serverId
    );
    setUploadedFiles(remainingFiles);
  };

  // If a user unchecks the "part of permit package" checkbox for a file that's being referenced in a permit condition, it triggers a warning popup.
  // This prevents a user from accidentally removing a referenced permit package file, which breaks the reference.
  const handleFinalPackageChange = (event, newValue, previousValue) => {
    if (previousValue && !newValue && isReferencedInConditions) {
      setConfirmUncheckOpen(true);
      return true;
    }
    return false;
  };

  const confirmUncheckFinalPackage = () => {
    dispatch(change(FORM.EDIT_NOTICE_OF_WORK_DOCUMENT_FORM, "is_final_package", false));
    setConfirmUncheckOpen(false);
  };

  const cancelUncheckFinalPackage = () => setConfirmUncheckOpen(false);

  return (
    <FormWrapper
      name={FORM.EDIT_NOTICE_OF_WORK_DOCUMENT_FORM}
      onSubmit={onSubmit}
      initialValues={{ now_application_guid, ...initialValues }}
      scrollOnToggleEdit={false}
      reduxFormConfig={{
        touchOnBlur: false,
        onSubmitSuccess: resetForm(FORM.EDIT_NOTICE_OF_WORK_DOCUMENT_FORM),
      }}
    >
      <Row gutter={16}>
        <Col span={24}>
          {!isEditMode && (
            <Field
              id="now_application_document_type_code"
              name="now_application_document_type_code"
              label="Document Category"
              placeholder="Select a document type"
              component={RenderSelect}
              data={filteredDropDownOptions}
              validate={[required]}
              required
            />
          )}
          <Field
            id="description"
            name="description"
            label="Description"
            component={RenderAutoSizeField}
            maximumCharacters={280}
            validate={maxLength(280)}
          />
          <Row gutter={16} align="middle">
            {!isInCompleteStatus && (
              <Popconfirm
                placement="topRight"
                title="This file is currently being referenced in a permit condition. Removing the file from the permit package will break that reference. Do you wish to continue?"
                open={confirmUncheckOpen}
                onConfirm={confirmUncheckFinalPackage}
                onCancel={cancelUncheckFinalPackage}
                okText="Yes"
                cancelText="No"
                trigger={[]}
              >
                <Col md={12} xs={24}>
                  <Field
                    id="is_final_package"
                    name="is_final_package"
                    label="Part of permit package"
                    type="checkbox"
                    component={RenderCheckbox}
                    onChange={handleFinalPackageChange}
                  />
                </Col>
              </Popconfirm>
            )}
            {is_final_package && isFeatureEnabled(Feature.INSPECTOR_PERMIT_PACKAGE_TYPE_SELECTOR) && (
              <Col md={isInCompleteStatus ? 24 : 12} xs={24}>
                <AuthorizationWrapper permission={Permission.EDIT_PERMITS} showToolTip={false}>
                  <Field
                    id="permit_package_document_type_code"
                    name="permit_package_document_type_code"
                    label="Document Type"
                    placeholder="Select a type"
                    component={RenderSelect}
                    data={PERMIT_PACKAGE_DOCUMENT_TYPE_OPTIONS}
                    validate={[required]}
                    required
                  />
                </AuthorizationWrapper>
              </Col>
            )}
          </Row>
          {is_final_package && (
            <>
              <Row gutter={16}>
                <Col md={12} xs={24}>
                  <Field
                    id="preamble_title"
                    name="preamble_title"
                    label="Title"
                    placeholder="Title"
                    component={RenderField}
                    validate={[required]}
                    required
                  />
                </Col>
                <Col md={12} xs={24}>
                  <Field
                    id="preamble_author"
                    name="preamble_author"
                    label="Author"
                    placeholder="Author"
                    component={RenderField}
                  />
                </Col>
              </Row>
              <Row gutter={16}>
                <Col md={12} xs={24}>
                  <Field
                    id="preamble_date"
                    name="preamble_date"
                    label="Date"
                    placeholder="Date"
                    component={RenderDate}
                  />
                </Col>
              </Row>
            </>
          )}
          {!isEditMode && (
            <Field
              id="notice_of_work_file_upload"
              name="uploadedFiles"
              component={RenderFileUpload}
              onFileLoad={onFileLoad}
              onRemoveFile={onRemoveFile}
              addFileStart={() => setDisabled(true)}
              uploadUrl={NOTICE_OF_WORK_DOCUMENT(now_application_guid)}
              acceptedFileTypesMap={{ ...DOCUMENT, ...EXCEL, ...IMAGE, ...SPATIAL }}
              listedFileTypes={["document", "spreadsheet", "image", "spatial"]}
              allowMultiple
              allowRevert
              label={<h5 style={{ display: "inline" }}>Document Upload</h5>}
              labelSubtitle="All files uploaded will be classified using the selected Category. To upload other
              file types, re-open this form after submitting the current files."
              abbrevLabel
              required
              validate={[requiredNewFiles]}
            />
          )}
        </Col>
      </Row>
      <div className="right center-mobile">
        <Popconfirm
          placement="topRight"
          title="Are you sure you want to cancel?"
          onConfirm={() => dispatch(closeModal())}
          okText="Yes"
          cancelText="No"
        >
          <Button className="full-mobile" type="default">
            Cancel
          </Button>
        </Popconfirm>
        <Button className="full-mobile" type="primary" htmlType="submit" disabled={disabled}>
          {title}
        </Button>
      </div>
    </FormWrapper>
  );
};
export default EditNoticeOfWorkDocumentForm;
